import { useEffect, useState, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const TABS = [
  { id: "connection", label: "Connection" },
  { id: "fields", label: "Field Mapping" },
  { id: "sync", label: "Sync" },
  { id: "campers", label: "Campers" },
  { id: "log", label: "Call Log" },
  { id: "health", label: "Health" },
];

export default function CampfrontAdmin() {
  const [activeTab, setActiveTab] = useState("connection");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-semibold text-[#1a3a6b]">Campfront Sync</h1>
          <a
            href="/"
            className="text-sm text-slate-600 hover:text-slate-900"
            data-testid="cf-back-link"
          >
            ← Back to grouping
          </a>
        </div>
      </header>

      <nav className="bg-white border-b border-slate-200 px-6">
        <div className="max-w-6xl mx-auto flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`cf-tab-${t.id}`}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === t.id
                  ? "border-[#1a3a6b] text-[#1a3a6b]"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {activeTab === "connection" && <ConnectionTab />}
        {activeTab === "fields" && <FieldMappingTab />}
        {activeTab === "sync" && <SyncTab />}
        {activeTab === "campers" && <CampersTab />}
        {activeTab === "log" && <CallLogTab />}
        {activeTab === "health" && <HealthTab />}
      </main>
    </div>
  );
}

// ---------- Tab 1: Connection ----------
function ConnectionTab() {
  const [status, setStatus] = useState(null);
  const [mcpUrl, setMcpUrl] = useState("https://mcp.campfront.com");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/campfront/oauth/status`);
      const d = await r.json();
      setStatus(d);
      if (d.mcp_url) setMcpUrl(d.mcp_url);
    } catch (e) {
      setErr("Failed to load status: " + e.message);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  async function saveUrl() {
    setSaving(true);
    setErr("");
    setMsg("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/mcp-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mcp_url: mcpUrl }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || "Save failed");
      }
      setMsg("MCP URL saved.");
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function registerClient() {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/oauth/register`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Register failed");
      setMsg(`Client registered: ${d.client_id}`);
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function connect() {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/oauth/start`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not start OAuth");
      window.open(d.authorize_url, "_blank", "noopener,noreferrer");
      setMsg("Authorization tab opened. After you click Allow in Campfront, this page will detect the connection.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/oauth/manual-refresh`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Refresh failed");
      setMsg(`Token refreshed (expires in ${d.expires_in}s).`);
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect from Campfront?")) return;
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/campfront/oauth/disconnect`, { method: "POST" });
      setMsg("Disconnected.");
      load();
    } finally {
      setBusy(false);
    }
  }

  if (!status) return <div className="text-slate-600">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Status</h2>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              status.connected ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
            }`}
            data-testid="cf-status-badge"
          >
            {status.connected ? "Connected" : "Not Connected"}
          </span>
        </div>
        {status.connected && (
          <div className="mt-3 text-sm text-slate-700 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><span className="text-slate-500">MCP URL:</span> {status.mcp_url}</div>
            <div><span className="text-slate-500">Authorized at:</span> {status.authorized_at}</div>
            <div><span className="text-slate-500">Token expires:</span> {status.access_token_expires_at}</div>
            <div><span className="text-slate-500">Scope:</span> {status.scope}</div>
            <div><span className="text-slate-500">Refresh count:</span> {status.refresh_count ?? 0}</div>
            <div className={status.refresh_failure_count ? "text-red-700" : ""}>
              <span className="text-slate-500">Refresh failures:</span> {status.refresh_failure_count ?? 0}
            </div>
            {status.last_refresh_error && (
              <div className="sm:col-span-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                Last refresh error: {status.last_refresh_error}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-1">Step 1: MCP Server URL</h2>
        <p className="text-sm text-slate-600 mb-3">
          Base URL of the Campfront MCP server. Default is{" "}
          <code className="bg-slate-100 px-1 rounded">https://mcp.campfront.com</code>. Change only if Campfront support directs you to.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={mcpUrl}
            onChange={(e) => setMcpUrl(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-300 rounded"
            data-testid="cf-mcp-url-input"
          />
          <button
            onClick={saveUrl}
            disabled={saving}
            className="px-4 py-2 bg-[#1a3a6b] text-white rounded font-medium disabled:opacity-50"
            data-testid="cf-save-url"
          >
            {saving ? "Saving…" : "Save URL"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3">Step 2: Register OAuth Client</h2>
        <p className="text-sm text-slate-600 mb-3">
          Dynamic registration (no secret). Required once per environment.
        </p>
        <button
          onClick={registerClient}
          disabled={busy || !status.mcp_url_set}
          className="px-4 py-2 bg-[#1a3a6b] text-white rounded font-medium disabled:opacity-50"
          data-testid="cf-register-client"
        >
          Register OAuth Client
        </button>
        {!status.mcp_url_set && (
          <p className="mt-2 text-xs text-amber-700">Save the MCP URL above first.</p>
        )}
        {status.client_registered && (
          <p className="mt-2 text-xs text-green-700">✓ Client registered</p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3">Step 3: Connect to Campfront</h2>
        <p className="text-sm text-slate-600 mb-3">
          Sign into Campfront in the same browser, then click below. After you click Allow in Campfront, this page detects the connection automatically.
        </p>
        {!status.connected ? (
          <button
            onClick={connect}
            disabled={busy || !status.client_registered}
            className="px-4 py-2 bg-[#1a3a6b] text-white rounded font-medium disabled:opacity-50"
            data-testid="cf-connect"
          >
            Connect to Campfront
          </button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button onClick={refresh} disabled={busy} className="px-4 py-2 bg-slate-200 text-slate-800 rounded font-medium" data-testid="cf-refresh-token">
              Refresh Token Now
            </button>
            <button onClick={disconnect} disabled={busy} className="px-4 py-2 bg-red-600 text-white rounded font-medium" data-testid="cf-disconnect">
              Disconnect
            </button>
          </div>
        )}
      </div>

      {msg && <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">{msg}</div>}
      {err && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{err}</div>}
    </div>
  );
}

// ---------- Tab 2: Field Mapping ----------
function FieldMappingTab() {
  const [mapping, setMapping] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState("");
  const [formFields, setFormFields] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/campfront/field-mapping`);
      const d = await r.json();
      setMapping(d);
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function discover() {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/discover-fields`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Discovery failed");
      setMsg("Discovery complete.");
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function loadForms() {
    try {
      const r = await fetch(`${API_URL}/api/campfront/forms`);
      const d = await r.json();
      setForms(d.forms || []);
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadFormFields(name) {
    setSelectedForm(name);
    try {
      const r = await fetch(`${API_URL}/api/campfront/form-fields/${encodeURIComponent(name)}`);
      const d = await r.json();
      setFormFields(d.fields || []);
    } catch (e) {
      setErr(e.message);
    }
  }

  async function setField(fieldKey, campfrontFieldId) {
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/field-mapping/${fieldKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campfront_field_id: campfrontFieldId,
          form_name: selectedForm,
        }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || "Save failed");
      }
      setMsg(`Mapped ${fieldKey}.`);
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const FIELD_DEFS = [
    { key: "group_field_id", label: "Group (required)", required: true },
    { key: "division_field_id", label: "Division (optional)", required: false },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Field Mapping</h2>
          <button onClick={discover} disabled={busy} className="px-3 py-1.5 bg-[#1a3a6b] text-white rounded text-sm" data-testid="cf-discover">
            {busy ? "Discovering…" : "Re-Discover Fields"}
          </button>
        </div>
        {mapping && (
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Form:</span> {mapping.form_name || "Not discovered"}</div>
            <div><span className="text-slate-500">Discovered at:</span> {mapping.discovered_at || "—"}</div>
            {mapping.warnings?.length > 0 && (
              <ul className="list-disc pl-5 text-amber-700">
                {mapping.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-semibold mb-3">Required Fields</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-slate-200">
              <th className="py-2">Field</th>
              <th className="py-2">Campfront Field ID</th>
            </tr>
          </thead>
          <tbody>
            {FIELD_DEFS.map((f) => (
              <tr key={f.key} className="border-b border-slate-100">
                <td className="py-2">{f.label}</td>
                <td className="py-2">
                  {mapping?.[f.key] ? (
                    <code className="bg-slate-100 px-2 py-0.5 rounded">{mapping[f.key]}</code>
                  ) : (
                    <span className="text-red-600 font-medium">Not Mapped</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-semibold mb-3">Manual Mapping</h3>
        <button onClick={loadForms} className="px-3 py-1.5 bg-slate-200 text-slate-800 rounded text-sm" data-testid="cf-load-forms">
          Load Campfront Forms
        </button>
        {forms.length > 0 && (
          <div className="mt-3">
            <label className="text-sm text-slate-600 block mb-1">Form:</label>
            <select
              value={selectedForm}
              onChange={(e) => loadFormFields(e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
              data-testid="cf-form-select"
            >
              <option value="">— Select —</option>
              {forms.map((f, i) => (
                <option key={i} value={f.name || f.label}>{f.name || f.label}</option>
              ))}
            </select>
          </div>
        )}
        {formFields.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-2">Click to map a field:</p>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded">
              <table className="w-full text-xs">
                <tbody>
                  {formFields.map((ff) => (
                    <tr key={ff.id} className="border-b border-slate-100">
                      <td className="px-2 py-1">{ff.label}</td>
                      <td className="px-2 py-1 text-slate-500"><code>{ff.id}</code></td>
                      <td className="px-2 py-1 text-right">
                        <button
                          onClick={() => setField("group_field_id", ff.id)}
                          className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded mr-1"
                        >
                          Set as Group
                        </button>
                        <button
                          onClick={() => setField("division_field_id", ff.id)}
                          className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded"
                        >
                          Set as Division
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {msg && <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">{msg}</div>}
      {err && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{err}</div>}
    </div>
  );
}

// ---------- Tab 3: Sync ----------
function SyncTab() {
  const [autoSync, setAutoSync] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [queue, setQueue] = useState([]);
  const [err, setErr] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/campfront/auto-sync-status`);
      const d = await r.json();
      setAutoSync(d.enabled);
      const q = await fetch(`${API_URL}/api/campfront/push-queue`).then((r) => r.json());
      setQueue(q.queue || []);
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const id = setInterval(loadStatus, 10000);
    return () => clearInterval(id);
  }, [loadStatus]);

  async function pullNow() {
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/pull-now`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Pull failed");
      setResult(d);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function pushAll() {
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API_URL}/api/campfront/push-all-pending`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Push failed");
      setResult({ ...d, mode: "push" });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleAuto(enabled) {
    await fetch(`${API_URL}/api/campfront/auto-sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    });
    setAutoSync(enabled);
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3">Auto-Sync</h2>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoSync}
            onChange={(e) => toggleAuto(e.target.checked)}
            className="w-4 h-4"
            data-testid="cf-auto-sync"
          />
          <span className="text-sm">Enabled — pull every 30 seconds; push immediately on group change</span>
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3">Manual Actions</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={pullNow} disabled={busy} className="px-4 py-2 bg-[#1a3a6b] text-white rounded" data-testid="cf-pull-now">
            {busy ? "Working…" : "Pull from Campfront Now"}
          </button>
          <button onClick={pushAll} disabled={busy} className="px-4 py-2 bg-slate-700 text-white rounded" data-testid="cf-push-all">
            Push All Pending
          </button>
        </div>
        {result && (
          <pre className="mt-4 p-3 bg-slate-50 rounded text-xs overflow-x-auto" data-testid="cf-sync-result">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3">Push Retry Queue ({queue.length})</h2>
        {queue.length === 0 ? (
          <p className="text-sm text-slate-500">No pending retries.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-left bg-slate-50">
                <tr>
                  <th className="px-2 py-1">Camper</th>
                  <th className="px-2 py-1">Group</th>
                  <th className="px-2 py-1">Attempts</th>
                  <th className="px-2 py-1">Next retry</th>
                  <th className="px-2 py-1">Error</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((q, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-2 py-1 font-mono text-[10px]">{q.local_camper_id?.slice(0, 8)}…</td>
                    <td className="px-2 py-1">{q.group_value || "—"}</td>
                    <td className="px-2 py-1">{q.attempts}</td>
                    <td className="px-2 py-1 text-slate-500">{q.next_retry_at}</td>
                    <td className="px-2 py-1 text-red-700">{q.last_error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {err && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{err}</div>}
    </div>
  );
}

// ---------- Tab 4: Campers ----------
function CampersTab() {
  const [campers, setCampers] = useState([]);
  const [filterUngrouped, setFilterUngrouped] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch(`${API_URL}/api/campfront/campers`)
      .then((r) => r.json())
      .then((d) => setCampers(d.campers || []));
  }, []);

  const filtered = campers.filter((c) => {
    if (filterUngrouped && c.assigned_group) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
      if (!name.includes(q) && !(c.campfront_enrollment_id || "").includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold">Campfront-Synced Campers ({filtered.length})</h2>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-2 py-1 border border-slate-300 rounded text-sm"
            data-testid="cf-camper-search"
          />
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={filterUngrouped}
              onChange={(e) => setFilterUngrouped(e.target.checked)}
            />
            Ungrouped only
          </label>
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500">No Campfront-synced campers yet. Use the Sync tab to pull.</p>
      ) : (
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-1">Name</th>
                <th className="px-2 py-1">Age</th>
                <th className="px-2 py-1">Division</th>
                <th className="px-2 py-1">Group</th>
                <th className="px-2 py-1">CF Enrollment</th>
                <th className="px-2 py-1">Last Pulled</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((c, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1">{c.first_name} {c.last_name}</td>
                  <td className="px-2 py-1">{c.age ?? "—"}</td>
                  <td className="px-2 py-1">{c.campfront_division || "—"}</td>
                  <td className="px-2 py-1 font-medium">{c.assigned_group || "—"}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{c.campfront_enrollment_id}</td>
                  <td className="px-2 py-1 text-slate-500">{c.campfront_last_pulled_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 500 && (
            <p className="text-xs text-slate-500 mt-2">Showing first 500 of {filtered.length}.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Tab 5: Call Log ----------
function CallLogTab() {
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState([]);
  const [filterTrig, setFilterTrig] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/campfront/call-log?limit=200`).then((r) => r.json());
    setCalls(r.calls || []);
    const s = await fetch(`${API_URL}/api/campfront/call-stats`).then((r) => r.json());
    setStats(s.stats || []);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = calls.filter((c) => {
    if (filterTrig !== "all" && c.triggered_by !== filterTrig) return false;
    if (filterStatus === "success" && !c.success) return false;
    if (filterStatus === "fail" && c.success) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3">Tool Stats</h2>
        {stats.length === 0 ? (
          <p className="text-sm text-slate-500">No calls logged yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left bg-slate-50">
              <tr>
                <th className="px-2 py-1">Tool</th>
                <th className="px-2 py-1">Count</th>
                <th className="px-2 py-1">Success</th>
                <th className="px-2 py-1">Fail</th>
                <th className="px-2 py-1">Avg ms</th>
                <th className="px-2 py-1">Max ms</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1 font-medium">{s._id}</td>
                  <td className="px-2 py-1">{s.count}</td>
                  <td className="px-2 py-1 text-green-700">{s.successes}</td>
                  <td className="px-2 py-1 text-red-700">{s.failures}</td>
                  <td className="px-2 py-1">{Math.round(s.avg_duration_ms || 0)}</td>
                  <td className="px-2 py-1">{s.max_duration_ms}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-lg font-semibold">Call Log ({filtered.length})</h2>
          <div className="flex gap-2">
            <select value={filterTrig} onChange={(e) => setFilterTrig(e.target.value)} className="px-2 py-1 text-sm border border-slate-300 rounded">
              <option value="all">All triggers</option>
              <option value="manual">Manual</option>
              <option value="auto_pull">Auto pull</option>
              <option value="auto_push">Auto push</option>
              <option value="scheduled">Scheduled</option>
              <option value="health_check">Health</option>
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-2 py-1 text-sm border border-slate-300 rounded">
              <option value="all">All</option>
              <option value="success">Success</option>
              <option value="fail">Failures</option>
            </select>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-1">Time</th>
                <th className="px-2 py-1">Tool</th>
                <th className="px-2 py-1">ms</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Trigger</th>
                <th className="px-2 py-1">Error</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} className="border-b border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setSelected(c)}>
                  <td className="px-2 py-1 text-slate-500">{c.called_at?.slice(11, 19)}</td>
                  <td className="px-2 py-1 font-medium">{c.tool_name}</td>
                  <td className="px-2 py-1">{c.duration_ms}</td>
                  <td className="px-2 py-1">{c.success ? <span className="text-green-700">✓</span> : <span className="text-red-700">✗</span>}</td>
                  <td className="px-2 py-1 text-slate-500">{c.triggered_by}</td>
                  <td className="px-2 py-1 text-red-700 truncate max-w-xs">{c.error || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-3">Call Details</h3>
            <pre className="bg-slate-50 p-3 rounded text-xs overflow-auto max-h-96">{JSON.stringify(selected, null, 2)}</pre>
            <button onClick={() => setSelected(null)} className="mt-3 px-4 py-1.5 bg-slate-200 rounded">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Tab 6: Health ----------
function HealthTab() {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [queue, setQueue] = useState([]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const h = await fetch(`${API_URL}/api/campfront/health`).then((r) => r.json());
      setHealth(h);
      const s = await fetch(`${API_URL}/api/campfront/oauth/status`).then((r) => r.json());
      setOauthStatus(s);
      const q = await fetch(`${API_URL}/api/campfront/push-queue`).then((r) => r.json());
      setQueue(q.queue || []);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Health</h2>
          <button onClick={load} disabled={busy} className="px-3 py-1.5 bg-[#1a3a6b] text-white rounded text-sm" data-testid="cf-health-check">
            {busy ? "Checking…" : "Run Health Check"}
          </button>
        </div>
        {health && (
          <div className="space-y-1 text-sm">
            <div>
              Status:{" "}
              <span className={`px-2 py-0.5 rounded font-medium ${health.healthy ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                {health.healthy ? "HEALTHY" : "DEGRADED"}
              </span>
            </div>
            {health.tool_count != null && <div><span className="text-slate-500">Tools available:</span> {health.tool_count}</div>}
            <div><span className="text-slate-500">Latency:</span> {health.duration_ms} ms</div>
            {health.error && <div className="text-red-700">Error: {health.error}</div>}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-semibold mb-2">Token</h3>
        {oauthStatus && (
          <div className="text-sm space-y-1">
            <div><span className="text-slate-500">Refresh count:</span> {oauthStatus.refresh_count ?? 0}</div>
            <div className={oauthStatus.refresh_failure_count ? "text-red-700" : ""}>
              <span className="text-slate-500">Refresh failures:</span> {oauthStatus.refresh_failure_count ?? 0}
            </div>
            <div><span className="text-slate-500">Last refreshed:</span> {oauthStatus.last_refreshed_at || "—"}</div>
            <div><span className="text-slate-500">Expires:</span> {oauthStatus.access_token_expires_at || "—"}</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-semibold mb-2">Push Queue</h3>
        <p className="text-sm">
          <span className="text-slate-500">Pending:</span> {queue.length}
          {queue.some((q) => q.requires_admin_review) && (
            <span className="ml-2 text-red-700 font-medium">⚠ Items need admin review</span>
          )}
        </p>
      </div>
    </div>
  );
}
